import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CustomDocumentSettings,
  CustomFieldDocument,
} from "../../models/DocumentSettings.model";
import {
  getCustomDocumentSettings,
  refreshDocumentSettings,
  createCustomDocument,
  createOrUpdateDocumentTemplate,
  updateDocumentStatus,
  generateDocSign,
  getDocSignRequest,
  completeDocSignRequest,
  createCustomDownloadDocument,
  createDocumentSendSign,
} from "../thunks/documentSettingsThunks";
import { CustomDocumentDTO } from "../../models/ProjectAccount.model";

interface DocumentSettingsState {
  // נתוני הגדרות מסמכים
  documentSettings: CustomDocumentSettings[];
  loading: boolean;
  error: string | null;

  // מסמך נבחר

  selectedDocument: CustomDocumentSettings | null;
  selectedDocumentId: number | null;

  // שדות מותאמים של המסמך הנבחר
  selectedDocumentFields: CustomFieldDocument[];

  // פעולות
  operationLoading: boolean;
  operationError: string | null;
  operationSuccess: boolean;
  operationMessage: string;

  // 🆕 יצירה/עדכון שבלונות
  templateFormLoading: boolean;
  templateFormError: string | null;
  templateFormSuccess: boolean;
  templateFormMessage: string;

  // 🆕 עדכון סטטוס מסמך
  // 🆕 הורדת מסמך
  downloadLoading: boolean;
  downloadError: string | null;
  downloadSuccess: boolean;
  downloadFileName: string | null;
  downloadFileBytes: Uint8Array | null;
  updateStatusLoading: boolean;
  updateStatusError: string | null;
  updateStatusSuccess: boolean;
  updateStatusMessage: string;

  // כללי
  initialized: boolean;
  lastFetched: string | null;

  // יצירת מסמך פרויקט
  createLoading: boolean;
  createError: string | null;
  createSuccess: boolean;
  createdDocument: CustomDocumentDTO | null;
  //חתימת מסמך
  generateDocSignLoading: boolean;
  generateDocSignError: string | null;
  generateDocSignSuccess: boolean;
  generateDocSignMessage: string;

  // 🆕 קבלת סטטוס חתימה
  getDocSignLoading: boolean;
  getDocSignError: string | null;
  docSignRequestData: any;

  // 🆕 השלמת חתימה
  completeDocSignLoading: boolean;
  completeDocSignError: string | null;
  completeDocSignSuccess: boolean;
  completeDocSignMessage: string;
}

const initialState: DocumentSettingsState = {
  downloadLoading: false,
  downloadError: null,
  downloadSuccess: false,
  downloadFileName: null,
  downloadFileBytes: null,

  documentSettings: [],
  loading: false,
  error: null,

  selectedDocument: null,
  selectedDocumentId: null,
  selectedDocumentFields: [],

  operationLoading: false,
  operationError: null,
  operationSuccess: false,
  operationMessage: "",

  templateFormLoading: false,
  templateFormError: null,
  templateFormSuccess: false,
  templateFormMessage: "",

  updateStatusLoading: false,
  updateStatusError: null,
  updateStatusSuccess: false,
  updateStatusMessage: "",

  initialized: false,
  lastFetched: null,

  createLoading: false,
  createError: null,
  createSuccess: false,
  createdDocument: null,
  generateDocSignLoading: false,
  generateDocSignError: null,
  generateDocSignSuccess: false,
  generateDocSignMessage: "",

  getDocSignLoading: false,
  getDocSignError: null,
  docSignRequestData: null,

  completeDocSignLoading: false,
  completeDocSignError: null,
  completeDocSignSuccess: false,
  completeDocSignMessage: "",
};

const documentSettingsSlice = createSlice({
  name: "documentSettings",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.operationError = null;
      state.operationMessage = "";
      state.operationSuccess = false;
      state.templateFormError = null;
      state.templateFormMessage = "";
      state.templateFormSuccess = false;
      // 🆕 נקה הודעות עדכון סטטוס
      state.updateStatusError = null;
      state.updateStatusMessage = "";
      state.updateStatusSuccess = false;
    },
    clearDownloadState(state) {
      state.downloadLoading = false;
      state.downloadError = null;
      state.downloadSuccess = false;
      state.downloadFileName = null;
      state.downloadFileBytes = null;
    },
    resetDocumentSettingsState: (state) => {
      Object.assign(state, initialState);
    },

    selectDocument: (state, action: PayloadAction<number>) => {
      const documentId = action.payload;
      const document = state.documentSettings.find(
        (doc) => doc.documentID === documentId,
      );

      if (document) {
        state.selectedDocument = document;
        state.selectedDocumentId = documentId;
        state.selectedDocumentFields =
          document.customFields?.filter((field) => field.isActive) || [];
      } else {
        state.selectedDocument = null;
        state.selectedDocumentId = null;
        state.selectedDocumentFields = [];
      }
    },

    clearSelectedDocument: (state) => {
      state.selectedDocument = null;
      state.selectedDocumentId = null;
      state.selectedDocumentFields = [];
    },

    setOperationSuccess: (state, action: PayloadAction<string>) => {
      state.operationSuccess = true;
      state.operationMessage = action.payload;
      state.operationError = null;
    },

    setOperationError: (state, action: PayloadAction<string>) => {
      state.operationSuccess = false;
      state.operationError = action.payload;
      state.operationMessage = "";
    },

    clearCreateMessages: (state) => {
      state.createError = null;
      state.createSuccess = false;
      state.createdDocument = null;
    },

    // 🆕 Actions עבור טופס שבלונות
    clearTemplateFormMessages: (state) => {
      state.templateFormError = null;
      state.templateFormSuccess = false;
      state.templateFormMessage = "";
    },

    // 🆕 Actions עבור עדכון סטטוס
    clearUpdateStatusMessages: (state) => {
      state.updateStatusError = null;
      state.updateStatusSuccess = false;
      state.updateStatusMessage = "";
    },

    clearError: (state) => {
      state.error = null;
      state.operationError = null;
      state.templateFormError = null;
    },

    resetInitialization: (state) => {
      state.initialized = false;
      state.error = null;
    },

    // 🆕 איפוס state של generateDocSign
    clearGenerateDocSignState: (state) => {
      state.generateDocSignLoading = false;
      state.generateDocSignError = null;
      state.generateDocSignSuccess = false;
      state.generateDocSignMessage = "";
    },

    // 🆕 איפוס state של getDocSign
    clearGetDocSignState: (state) => {
      state.getDocSignLoading = false;
      state.getDocSignError = null;
      state.docSignRequestData = null;
    },

    // 🆕 איפוס state של completeDocSign
    clearCompleteDocSignState: (state) => {
      state.completeDocSignLoading = false;
      state.completeDocSignError = null;
      state.completeDocSignSuccess = false;
      state.completeDocSignMessage = "";
    },
  },

  extraReducers: (builder) => {
    builder
      // Get Custom Document Settings
      .addCase(getCustomDocumentSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.operationError = null;
      })
      .addCase(getCustomDocumentSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.documentSettings = action.payload.customDocumentSettings || [];
        state.initialized = true;
        state.lastFetched = new Date().toISOString();
        state.error = null;

        if (state.selectedDocumentId) {
          const document = state.documentSettings.find(
            (doc) => doc.documentID === state.selectedDocumentId,
          );
          if (document) {
            state.selectedDocument = document;
            state.selectedDocumentFields =
              document.customFields?.filter((field) => field.isActive) || [];
          }
        }
      })
      .addCase(getCustomDocumentSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch document settings";
        state.initialized = true;
      })

      // 🆕 Create/Update Document Template
      .addCase(createOrUpdateDocumentTemplate.pending, (state) => {
        state.templateFormLoading = true;
        state.templateFormError = null;
        state.templateFormSuccess = false;
        state.templateFormMessage = "";
      })
      .addCase(createOrUpdateDocumentTemplate.fulfilled, (state, action) => {
        state.templateFormLoading = false;
        state.templateFormSuccess = true;
        state.templateFormMessage =
          action.payload.message || "שבלונה נשמרה בהצלחה";
        state.templateFormError = null;
      })
      .addCase(createOrUpdateDocumentTemplate.rejected, (state, action) => {
        state.templateFormLoading = false;
        state.templateFormSuccess = false;
        state.templateFormError = action.payload || "שגיאה בשמירת השבלונה";
        state.templateFormMessage = "";
      })

      // Refresh Document Settings
      .addCase(refreshDocumentSettings.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(refreshDocumentSettings.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.documentSettings = action.payload.customDocumentSettings || [];
        state.operationSuccess = true;
        state.operationMessage = "הגדרות מסמכים רוענו בהצלחה";
        state.lastFetched = new Date().toISOString();

        if (state.selectedDocumentId) {
          const document = state.documentSettings.find(
            (doc) => doc.documentID === state.selectedDocumentId,
          );
          if (document) {
            state.selectedDocument = document;
            state.selectedDocumentFields =
              document.customFields?.filter((field) => field.isActive) || [];
          }
        }
      })
      .addCase(refreshDocumentSettings.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          action.payload || "Failed to refresh document settings";
      })

      // Create Custom Document
      .addCase(createCustomDocument.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.createSuccess = false;
      })
      .addCase(createCustomDocument.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.createdDocument = action.payload.customDocument;
        state.operationSuccess = true;
        state.operationMessage = "המסמך נוצר בהצלחה";
      })
      .addCase(createCustomDocument.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.createError = action.payload || "Failed to create document";
      })

      // 🆕 Update Document Status
      .addCase(updateDocumentStatus.pending, (state) => {
        state.updateStatusLoading = true;
        state.updateStatusError = null;
        state.updateStatusSuccess = false;
        state.updateStatusMessage = "";
      })
      .addCase(updateDocumentStatus.fulfilled, (state, action) => {
        state.updateStatusLoading = false;
        state.updateStatusSuccess = true;
        state.updateStatusMessage =
          action.payload.message || "סטטוס המסמך עודכן בהצלחה";
        state.updateStatusError = null;
      })
      .addCase(updateDocumentStatus.rejected, (state, action) => {
        state.updateStatusLoading = false;
        state.updateStatusSuccess = false;
        state.updateStatusError = action.payload || "שגיאה בעדכון סטטוס המסמך";
        state.updateStatusMessage = "";
      })
      .addCase(generateDocSign.pending, (state) => {
        state.generateDocSignLoading = true;
        state.generateDocSignError = null;
        state.generateDocSignSuccess = false;
        state.generateDocSignMessage = "";
      })
      .addCase(generateDocSign.fulfilled, (state, action) => {
        state.generateDocSignLoading = false;
        state.generateDocSignSuccess = true;
        state.generateDocSignMessage =
          action.payload.message || "חתימת המסמך נוצרה בהצלחה";
        state.generateDocSignError = null;
      })
      .addCase(generateDocSign.rejected, (state, action) => {
        state.generateDocSignLoading = false;
        state.generateDocSignSuccess = false;
        state.generateDocSignError =
          action.payload || "שגיאה ביצירת חתימת המסמך";
        state.generateDocSignMessage = action.error.message || "";
      })

      // 🆕 Get Doc Sign Request
      .addCase(getDocSignRequest.pending, (state) => {
        state.getDocSignLoading = true;
        state.getDocSignError = null;
      })
      .addCase(getDocSignRequest.fulfilled, (state, action) => {
        state.getDocSignLoading = false;
        state.docSignRequestData = action.payload.documentSignRequestDTO;
        state.getDocSignError = null;
      })
      .addCase(getDocSignRequest.rejected, (state, action) => {
        state.getDocSignLoading = false;
        state.getDocSignError = action.payload || "שגיאה בקבלת סטטוס החתימה";
        state.docSignRequestData = null;
      })

      // 🆕 Complete Doc Sign Request
      .addCase(completeDocSignRequest.pending, (state) => {
        state.completeDocSignLoading = true;
        state.completeDocSignError = null;
        state.completeDocSignSuccess = false;
        state.completeDocSignMessage = "";
      })
      .addCase(completeDocSignRequest.fulfilled, (state, action) => {
        state.completeDocSignLoading = false;
        state.completeDocSignSuccess = true;
        state.completeDocSignMessage =
          action.payload.message || "חתימה הושלמה בהצלחה";
        state.completeDocSignError = null;
      })
      .addCase(completeDocSignRequest.rejected, (state, action) => {
        state.completeDocSignLoading = false;
        state.completeDocSignSuccess = false;
        state.completeDocSignError = action.payload || "שגיאה בהשלמת החתימה";
        state.completeDocSignMessage = "";
      })
      // 🆕 Create and Download Custom Document
      .addCase(createCustomDownloadDocument.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
        state.downloadSuccess = false;
        state.downloadFileName = null;
        state.downloadFileBytes = null;
      })
      .addCase(createCustomDownloadDocument.fulfilled, (state) => {
        state.downloadLoading = false;
        state.downloadSuccess = true;
      })
      .addCase(createCustomDownloadDocument.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadSuccess = false;
        state.downloadError = action.payload || "שגיאה בהורדת המסמך";
        state.downloadFileName = null;
        state.downloadFileBytes = null;
      })

      // 🆕 Create Document and Send to Sign
      .addCase(createDocumentSendSign.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.createSuccess = false;
      })
      .addCase(createDocumentSendSign.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.createdDocument = action.payload.customDocument;
        state.operationSuccess = true;
        state.operationMessage = "המסמך נוצר ונשלח לחתימה בהצלחה";
      })
      .addCase(createDocumentSendSign.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.createError = action.payload || "שגיאה ביצירת ושליחת המסמך";
      });
  },
});

export const {
  clearMessages,
  resetDocumentSettingsState,
  selectDocument,
  clearSelectedDocument,
  setOperationSuccess,
  setOperationError,
  clearCreateMessages,
  clearTemplateFormMessages,
  clearUpdateStatusMessages,
  clearError,
  resetInitialization,
  clearGenerateDocSignState,
  clearGetDocSignState,
  clearCompleteDocSignState,
} = documentSettingsSlice.actions;

export default documentSettingsSlice.reducer;

// ===== SELECTORS =====

// בסיסיים
export const selectDocumentSettings = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.documentSettings;

export const selectDocumentSettingsLoading = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.loading;

export const selectDocumentSettingsError = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.error;

export const selectDocumentSettingsInitialized = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.initialized;

// 🆕 Selectors עבור טופס שבלונות
export const selectTemplateFormLoading = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.templateFormLoading;

export const selectTemplateFormError = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.templateFormError;

export const selectTemplateFormSuccess = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.templateFormSuccess;

export const selectTemplateFormMessage = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.templateFormMessage;

// מסמך נבחר
export const selectSelectedDocument = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.selectedDocument;

export const selectSelectedDocumentId = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.selectedDocumentId;

export const selectSelectedDocumentFields = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.selectedDocumentFields;

// פעולות
export const selectOperationLoading = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.operationLoading;

export const selectOperationError = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.operationError;

export const selectOperationSuccess = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.operationSuccess;

export const selectOperationMessage = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.operationMessage;

// כללי
export const selectLastFetched = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.lastFetched;

// יצירת מסמך
export const selectCreateLoading = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.createLoading;

export const selectCreateError = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.createError;

export const selectCreateSuccess = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.createSuccess;

export const selectCreatedDocument = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.createdDocument;

// 🆕 Selectors עבור עדכון סטטוס
export const selectUpdateStatusLoading = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.updateStatusLoading;

export const selectUpdateStatusError = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.updateStatusError;

export const selectUpdateStatusSuccess = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.updateStatusSuccess;

export const selectUpdateStatusMessage = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.updateStatusMessage;

// Computed selectors
export const selectDocumentById =
  (documentId: number) =>
  (state: { documentSettings: DocumentSettingsState }) =>
    state.documentSettings.documentSettings.find(
      (document) => document.documentID === documentId,
    );

export const selectActiveDocuments = (state: {
  documentSettings: DocumentSettingsState;
}) =>
  state.documentSettings.documentSettings.filter(
    (document) => document.isActive,
  );

export const selectQuickAccessDocuments = (state: {
  documentSettings: DocumentSettingsState;
}) =>
  state.documentSettings.documentSettings.filter(
    (document) => document.quickAccess,
  );

// 🆕 Selectors עבור קבלת סטטוס חתימה
export const selectGetDocSignLoading = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.getDocSignLoading;

export const selectGetDocSignError = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.getDocSignError;

export const selectDocSignRequestData = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.docSignRequestData;

// 🆕 Selectors עבור השלמת חתימה
export const selectCompleteDocSignLoading = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.completeDocSignLoading;

export const selectCompleteDocSignError = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.completeDocSignError;

export const selectCompleteDocSignSuccess = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.completeDocSignSuccess;

export const selectCompleteDocSignMessage = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.completeDocSignMessage;

// 🆕 Selectors עבור generateDocSign
export const selectGenerateDocSignLoading = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.generateDocSignLoading;

export const selectGenerateDocSignError = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.generateDocSignError;

export const selectGenerateDocSignSuccess = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.generateDocSignSuccess;

export const selectGenerateDocSignMessage = (state: {
  documentSettings: DocumentSettingsState;
}) => state.documentSettings.generateDocSignMessage;
